import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Radio, Users, LogIn, Loader2, AlertCircle, Sparkles, Lock, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AuthModal from "../components/AuthModal";
import { TrackBadge } from "../components/TrackBadge";

const API = "/api";

export default function LiveJoin({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(searchParams.get("code") || "");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");
  const [joinFailed, setJoinFailed] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const activeCode = useRef(""); // ignore results for a code the student has since changed

  // A valid, open code joins straight away - no extra button press
  const joinSession = useCallback(async (joinCode) => {
    setJoining(true);
    setJoinFailed(false);
    try {
      const res = await fetch(`${API}/live/join/${joinCode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (activeCode.current !== joinCode) return;
      if (res.ok) {
        sessionStorage.setItem("liveDrillSession", JSON.stringify({
          session_id: data.session_id,
          participant_id: data.participant_id,
          participant_token: data.participant_token,
          nickname: data.nickname,
          is_anonymous: data.is_anonymous,
        }));
        navigate("/live/play");
        return;
      }
      if (res.status === 403) {
        // kicked / locked / full since the check - show the reason inline
        setError(data.detail || "You can't join this session right now");
        if (data.reason !== "kicked") validateCode(joinCode, false);
        return;
      }
      toast.error(data.detail || "Could not join the session");
      setJoinFailed(true);
    } catch {
      if (activeCode.current !== joinCode) return;
      toast.error("Could not join the session. Please try again.");
      setJoinFailed(true);
    } finally {
      if (activeCode.current === joinCode) setJoining(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const validateCode = useCallback(async (joinCode, autoJoin = true) => {
    activeCode.current = joinCode;
    setChecking(true);
    setError("");
    setJoinFailed(false);
    setSessionInfo(null);
    try {
      const res = await fetch(`${API}/live/join/${joinCode}`);
      const data = await res.json();
      if (activeCode.current !== joinCode) return;
      if (!res.ok) {
        setError(data.detail || "No active session found with this code");
        return;
      }
      setSessionInfo(data);
      if (autoJoin && !data.locked && !data.is_full) joinSession(joinCode);
    } catch {
      if (activeCode.current === joinCode) setError("Could not check the code. Please try again.");
    } finally {
      if (activeCode.current === joinCode) setChecking(false);
    }
  }, [joinSession]);

  useEffect(() => {
    // Resume existing live session if present
    try {
      const saved = JSON.parse(sessionStorage.getItem("liveDrillSession"));
      if (saved?.session_id && saved?.participant_token) {
        navigate("/live/play");
        return;
      }
    } catch { /* ignore */ }
    if (code.length === 6) validateCode(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCodeChange = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 6);
    setCode(cleaned);
    activeCode.current = cleaned;
    setError("");
    setJoinFailed(false);
    setSessionInfo(null);
    if (cleaned.length === 6) validateCode(cleaned);
  };

  const busy = checking || joining;

  return (
    <div className="max-w-lg mx-auto px-4 py-10 sm:py-16" data-testid="live-join-page">
      <div className="text-center mb-8">
        <div className="inline-flex p-3 rounded-2xl bg-primary/10 mb-4">
          <Radio className="w-8 h-8 text-primary" />
        </div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl mb-2">Join Live Drill</h1>
        <p className="text-muted-foreground text-sm">Enter the 6-digit code from your teacher's screen - you'll join the moment it's right</p>
      </div>

      <div className="bg-card rounded-2xl border border-border p-6 shadow-sm space-y-5">
        <div>
          <label className="text-sm font-medium text-muted-foreground block mb-2">Join Code</label>
          <Input
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            placeholder="000000"
            inputMode="numeric"
            autoFocus
            disabled={joining}
            className="text-center text-3xl font-mono tracking-[0.5em] h-16 rounded-xl"
            data-testid="live-join-code-input"
          />
        </div>

        {busy && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid={joining ? "live-join-joining" : "live-join-checking"}>
            <Loader2 className="w-4 h-4 animate-spin" />
            {joining ? `Joining ${sessionInfo?.drill_title || "the session"}...` : "Checking code..."}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400" data-testid="live-join-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        {sessionInfo && (
          <div className="space-y-4" data-testid="live-join-session-info">
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
              <p className="font-semibold text-foreground flex items-center gap-2 flex-wrap">{sessionInfo.drill_title}<TrackBadge mode={sessionInfo.mode} testId="live-join-track-badge" /></p>
              <p className="text-sm text-muted-foreground">{sessionInfo.school_name}</p>
              <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                <Users className="w-3.5 h-3.5" />
                {sessionInfo.participant_count} / {sessionInfo.max_participants} joined
                <span className="mx-1">•</span>
                {sessionInfo.total_questions} questions
              </div>
            </div>

            {sessionInfo.locked ? (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400" data-testid="live-join-locked-msg">
                <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                This session is locked - the teacher isn't accepting new players right now.
              </div>
            ) : sessionInfo.is_full ? (
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400" data-testid="live-join-full-msg">
                This session is full. Ask your teacher for a spot!
              </div>
            ) : joinFailed ? (
              <Button
                className="w-full h-12 rounded-xl text-base gap-2"
                onClick={() => joinSession(code)}
                disabled={joining}
                data-testid="live-join-retry-btn"
              >
                <RefreshCw className="w-5 h-5" />
                Try joining again
              </Button>
            ) : null}
          </div>
        )}

        {!user && !joining && (
          <div className="p-3 rounded-xl bg-muted/60 border border-border space-y-2" data-testid="live-join-guest-msg">
            <p className="text-sm text-muted-foreground">
              Not logged in? You'll join as a <strong>guest</strong> with a fun nickname - points won't be saved to an account.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={() => setShowAuthModal(true)}
              data-testid="live-join-login-btn"
            >
              <LogIn className="w-4 h-4" />
              Log in first to save your points
            </Button>
          </div>
        )}
        {user && !sessionInfo && !busy && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400" data-testid="live-join-logged-in-msg">
            <Sparkles className="w-4 h-4 shrink-0" />
            You'll join as <strong>{user.nickname || user.name}</strong> - points you earn will be added to your score!
          </div>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </div>
  );
}
